import { rem } from "polished";
import styled from "styled-components";

import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import type { ImageFit } from "@/components/editor/shared/header/header-settings-component";
import { withViewportStyle } from "@/components/editor/shared/with-viewport-style";
import { baseButtonStyle } from "@/components/editor/themes/crosby/constants";

export const Container = withViewportStyle("div")`
  padding: ${rem(68)} ${rem(24)};
  display: grid;

  ${(props) => props.$viewport.tablet`
    padding-top: ${rem(60)};
  `}

  ${(props) => props.$viewport.mobile`
    padding: ${rem(24)} ${rem(16)} ${rem(16)};
  `}
`;

export const ContentWrapper = withViewportStyle("div")<{
  isLogoShowing: boolean;
}>`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: ${rem(24)};

  .text-primitive {
    text-align: left;
  }

  .ck-content h1,
  .ck-content h2,
  .ck-content h3,
  .ck-content h4,
  .ck-content h5,
  .ck-content p {
    margin: 0;
  }

  .background-squiggle {
    position: absolute;
    top: -50px;
    right: -210px;
    transform: rotate(-120deg);
  }

  ${(props) => props.$viewport.tablet`
    grid-template-columns: 1fr;
    grid-gap: ${rem(24)};
    grid-template-areas:
      "heading"
      "subheading"
      "ctaText"
      "button";

      .text-primitive,
      .ck-content h1,
      .ck-content h2,
      .ck-content h3,
      .ck-content h4,
      .ck-content h5,
      .ck-content p {
        text-align: center;
      }
  `}

  ${(props) => props.$viewport.mobile`
    grid-gap: ${rem(16)};
    margin-top: ${(props) => (props.isLogoShowing ? 0 : rem(24))};
  `}
`;

export const LeftColumn = withViewportStyle("div")`
  display: grid;
  grid-template-areas:
    "heading"
    "ctaText";
  grid-template-rows: min-content min-content;
  grid-row-gap: ${rem(40)};

  ${(props) => props.$viewport.tablet`
    display: contents;
  `}
`;

export const RightColumn = withViewportStyle("div")`
  display: grid;
  grid-template-areas:
    "subheading"
    "button";
  grid-template-rows: min-content min-content;
  grid-row-gap: ${rem(32)};

  ${(props) => props.$viewport.tablet`
    display: contents;
  `}
`;

export const LogoWrapper = withViewportStyle("div")`
  height: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  
  ${(props) => props.$viewport.tablet`
    justify-content: center;
  `}
`;

export const Logo = withViewportStyle("img")<{ isLogoResized: boolean }>`
  max-height: ${({ isLogoResized }) => (isLogoResized ? rem(120) : rem(40))};
  max-width: 100%;
  object-fit: contain;
  margin-bottom: ${rem(32)};

  ${(props) => props.$viewport.tablet`
    max-height: ${({ isLogoResized }) => (isLogoResized ? rem(100) : rem(35))};
  `}

  ${(props) => props.$viewport.mobile`
    max-height: ${({ isLogoResized }) => (isLogoResized ? rem(80) : rem(31))};
    margin-bottom: ${rem(24)};
  `}
`;

export const ButtonWrapper = withViewportStyle("div")`
  grid-area: button;
  width: 100%;
  max-width: ${rem(180)};

  .button-primitive {
    ${baseButtonStyle}
    width: 100%;
  }
  
  ${(props) => props.$viewport.tablet`
    margin: 0 auto;
  `}
`;

export const Image = withViewportStyle("img")<{
  $position: ImagePosition;
  imageFit?: ImageFit;
}>`
  width: 100%;
  height: 100%;
  object-fit: ${(props) => props.imageFit || "cover"};
  object-position: ${(props) => props.$position};
  overflow: hidden;
`;

export const ImageWrapper = withViewportStyle("div")`
  height: ${rem(360)};

  ${(props) => props.$viewport.tablet`
    height: ${rem(280)};
  `}

  ${(props) => props.$viewport.mobile`
    height: ${rem(173)};
  `}
`;

export const HeadingWrapper = styled.div`
  grid-area: heading;
`;

export const CTATextWrapper = withViewportStyle("div")`
  grid-area: ctaText;

  ${(props) => props.$viewport.mobile`
    margin-bottom: ${rem(8)};
  `}
`;

export const SubheadingWrapper = withViewportStyle("div")`
  grid-area: subheading;

  ${(props) => props.$viewport.mobile`
    margin-bottom: ${rem(8)};
  `}
`;
